import { useCallback } from 'react';
import { AnalyticsEvent } from '../types/community';

/**
 * Analytics Hook
 * Provides methods to track user events throughout the application
 * Currently logs to console - integrate with PostHog/Mixpanel/etc in production
 */

class AnalyticsService {
  private sessionId: string;
  private userId?: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  track(eventName: string, properties: Record<string, unknown> = {}) {
    const event: AnalyticsEvent = {
      event_name: eventName,
      properties,
      timestamp: new Date().toISOString(),
      user_id: this.userId,
      session_id: this.sessionId,
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', event);
    }

    // TODO: Send to analytics service
    // Example with PostHog:
    // posthog.capture(eventName, properties);

    // Example with Mixpanel:
    // mixpanel.track(eventName, properties);

    // Store in localStorage for debugging
    this.storeEvent(event);
  }

  private storeEvent(event: AnalyticsEvent) {
    try {
      const events = JSON.parse(
        localStorage.getItem('analytics_events') || '[]'
      );
      events.push(event);
      // Keep only last 100 events
      const recentEvents = events.slice(-100);
      localStorage.setItem('analytics_events', JSON.stringify(recentEvents));
    } catch (error) {
      console.error('Failed to store analytics event:', error);
    }
  }

  // Page view tracking
  pageView(pageName: string, properties: Record<string, unknown> = {}) {
    this.track('page_view', {
      page_name: pageName,
      ...properties,
    });
  }

  // Common event helpers
  searchPerformed(
    query: string,
    resultsCount: number,
    filters: Record<string, unknown>
  ) {
    this.track('park_search', {
      query,
      results_count: resultsCount,
      filters_count: Object.keys(filters).filter((k) => Boolean(filters[k]))
        .length,
      filters,
    });
  }

  parkViewed(parkId: string, parkName: string, source: 'map' | 'list') {
    this.track('park_view', {
      park_id: parkId,
      park_name: parkName,
      source,
    });
  }

  reviewSubmitted(
    parkId: string,
    rating: number,
    hasPhotos: boolean,
    characterCount: number
  ) {
    this.track('review_submit', {
      park_id: parkId,
      rating,
      has_photos: hasPhotos,
      character_count: characterCount,
    });
  }

  photoUploaded(parkId: string, photoCount: number) {
    this.track('photo_upload', {
      park_id: parkId,
      photo_count: photoCount,
    });
  }

  meetupCreated(
    parkId: string,
    participantsLimit: number,
    daysInFuture: number
  ) {
    this.track('meetup_create', {
      park_id: parkId,
      participants_limit: participantsLimit,
      days_in_future: daysInFuture,
    });
  }

  meetupRSVP(meetupId: string, status: 'attending' | 'maybe' | 'declined') {
    this.track('meetup_rsvp', {
      meetup_id: meetupId,
      status,
    });
  }

  contentReported(contentType: string, reason: string) {
    this.track('content_report', {
      content_type: contentType,
      reason,
    });
  }

  walkingBuddyEnabled() {
    this.track('walking_buddy_enable');
  }

  walkingBuddyMatchViewed(matchCount: number) {
    this.track('walking_buddy_match_view', {
      match_count: matchCount,
    });
  }

  messageSent(conversationId: string) {
    this.track('message_sent', {
      conversation_id: conversationId,
    });
  }

  filterApplied(filterName: string, value: unknown) {
    this.track('filter_apply', {
      filter_name: filterName,
      value,
    });
  }

  mapInteraction(action: 'zoom_in' | 'zoom_out' | 'center' | 'pin_click') {
    this.track('map_interaction', {
      action,
    });
  }
}

const analytics = new AnalyticsService();

export const useAnalytics = () => {
  const track = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      analytics.track(eventName, properties);
    },
    []
  );

  const pageView = useCallback(
    (pageName: string, properties?: Record<string, unknown>) => {
      analytics.pageView(pageName, properties);
    },
    []
  );

  const setUserId = useCallback((userId: string) => {
    analytics.setUserId(userId);
  }, []);

  return {
    track,
    pageView,
    setUserId,
    // Event-specific helpers
    searchPerformed: analytics.searchPerformed.bind(analytics),
    parkViewed: analytics.parkViewed.bind(analytics),
    reviewSubmitted: analytics.reviewSubmitted.bind(analytics),
    photoUploaded: analytics.photoUploaded.bind(analytics),
    meetupCreated: analytics.meetupCreated.bind(analytics),
    meetupRSVP: analytics.meetupRSVP.bind(analytics),
    contentReported: analytics.contentReported.bind(analytics),
    walkingBuddyEnabled: analytics.walkingBuddyEnabled.bind(analytics),
    walkingBuddyMatchViewed: analytics.walkingBuddyMatchViewed.bind(analytics),
    messageSent: analytics.messageSent.bind(analytics),
    filterApplied: analytics.filterApplied.bind(analytics),
    mapInteraction: analytics.mapInteraction.bind(analytics),
  };
};

export default analytics;
